import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useAuthStore } from '@/lib/auth.store';
import { listMyCharts } from '@/lib/chart.api';
import { dailyFortuneApi, type DailyFortune, type PushSubscription } from '@/lib/stage4.api';

export function PushSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const [sub, setSub] = useState<PushSubscription | null>(null);
  const [charts, setCharts] = useState<
    Array<{ id: string; displayName: string; baziKey: string; gender: string }>
  >([]);
  const [today, setToday] = useState<DailyFortune | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    void dailyFortuneApi
      .getSubscription()
      .then(setSub)
      .catch(() => setSub(null));
    void listMyCharts().then((list) =>
      setCharts(
        list.map((c) => ({
          id: c.id,
          displayName: c.displayName,
          baziKey: c.baziKey,
          gender: c.gender,
        })),
      ),
    );
  }, [user]);

  const update = async (
    patch: Partial<{ emailEnabled: boolean; pushHour: number; primaryChartId: string | null }>,
  ) => {
    setSaving(true);
    try {
      const next = await dailyFortuneApi.setSubscription(patch);
      setSub(next);
    } finally {
      setSaving(false);
    }
  };

  const refreshToday = async () => {
    if (!sub?.primaryChartId) return;
    try {
      const t = await dailyFortuneApi.today(sub.primaryChartId);
      setToday(t);
    } catch (err) {
      void err;
    }
  };

  useEffect(() => {
    if (sub?.primaryChartId) void refreshToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub?.primaryChartId]);

  if (!user) {
    return (
      <main className="flex flex-1 items-center justify-center px-4 sm:px-6">
        <Card className="max-w-md p-6 text-center sm:p-10">
          <p className="font-serif text-2xl text-fg mb-3">每日运势推送</p>
          <Link to="/auth/login">
            <Button size="lg">登录</Button>
          </Link>
        </Card>
      </main>
    );
  }

  return (
    <main className="flex-1 px-4 py-8 sm:px-6 sm:py-12">
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="font-serif text-3xl text-fg">推送设置</h1>
        <p className="text-sm text-fg-muted">每日 8:00 通过邮件发送当日运势（基于你的主命盘）</p>

        <Card className="space-y-5 p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-fg">邮件推送</p>
              <p className="text-xs text-fg-muted mt-1">每日运势 + 节气提醒</p>
            </div>
            <Toggle
              checked={!!sub?.emailEnabled}
              onChange={(v) => update({ emailEnabled: v })}
              disabled={saving}
            />
          </div>

          <div>
            <label className="text-sm text-fg block mb-2">主命盘</label>
            <select
              value={sub?.primaryChartId ?? ''}
              onChange={(e) => update({ primaryChartId: e.target.value || null })}
              className="w-full bg-bg-1 border border-border rounded px-3 py-2 text-sm text-fg"
              disabled={saving}
            >
              <option value="">使用最早创建的命盘</option>
              {charts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName} ·{' '}
                  {c.gender === 'male' ? '男' : c.gender === 'female' ? '女' : '未指定'} ·{' '}
                  {c.baziKey}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-fg block mb-2">推送时间</label>
            <select
              value={sub?.pushHour ?? 8}
              onChange={(e) => update({ pushHour: Number(e.target.value) })}
              className="w-full bg-bg-1 border border-border rounded px-3 py-2 text-sm text-fg"
              disabled={saving}
            >
              {[7, 8, 9, 10, 12, 18, 20].map((h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, '0')}:00
                </option>
              ))}
            </select>
            <p className="text-xs text-fg-muted mt-1">
              当前按系统统一时段推送，个性化时间将逐步开放
            </p>
          </div>

          {sub?.lastPushedDate && (
            <p className="text-xs text-fg-muted">上次推送：{sub.lastPushedDate}</p>
          )}
        </Card>

        {today?.content && (
          <Card variant="gold" className="p-6">
            <p className="text-xs text-accent tracking-widest mb-3">
              今日预览 · {today.date}
              {today.dayGanZhi ? ` · ${today.dayGanZhi}` : ''}
              {today.jieqiName ? ` · ${today.jieqiName}` : ''}
            </p>
            <p className="font-serif text-xl text-fg mb-4">{today.content.oneliner}</p>
            <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <div>
                <p className="text-xs text-accent mb-1">宜</p>
                <ul className="space-y-1 text-fg-2">
                  {today.content.favor.map((x, i) => (
                    <li key={i}>· {x}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs text-fg-muted mb-1">忌</p>
                <ul className="space-y-1 text-fg-2">
                  {today.content.avoid.map((x, i) => (
                    <li key={i}>· {x}</li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="text-sm text-fg mt-4">
              <span className="text-accent">落子：</span>
              {today.content.action}
            </p>
          </Card>
        )}
      </div>
    </main>
  );
}

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-fast ' +
        (checked ? 'bg-accent' : 'bg-bg-2')
      }
    >
      <span
        className={
          'inline-block h-4 w-4 rounded-full bg-fg-on-accent transition-transform ' +
          (checked ? 'translate-x-6' : 'translate-x-1')
        }
      />
    </button>
  );
}
